import Dialog from "@components/dialog/Dialog";
import { createBindingContext, IEntity, TEntityKey } from "@odata/BindingContext";
import { EntitySetName, EntityTypeName, IChatEntity } from "@odata/GeneratedEntityTypes";
import { OData } from "@odata/OData";
import { getDialogDefinitions, IChatCustomData } from "@pages/chats/Chats.def";
import { ISendToAccountantChatData } from "@pages/chats/Chats.utils";
import ChatsTableView from "@pages/chats/ChatsTableView";
import DialogPage from "@pages/DialogPage";
import { isNotDefined } from "@utils/general";
import i18next from "i18next";
import React, { useMemo } from "react";
import PlugAndPlayForm from "views/formView/PlugAndPlayForm";

import { PageViewMode } from "../../enums";
import { StorageModel } from "../../model/StorageModel";
import { TableStorage } from "../../model/TableStorage";

export interface IProps {
    storage?: StorageModel;
    user: IEntity;
    id?: TEntityKey;
    isOpen?: boolean;
    isDraft?: boolean;
    inboxName?: string;
    onClose: () => void;
    oData?: OData;
    chatData?: ISendToAccountantChatData;
    useCustomMessages?: boolean;
    isInbox?: boolean;
}

export const ChatDialog: React.FC<IProps> = (args) => {

    const getDefArgs = useMemo(() => ({ isDialog: true }), []);

    if (isNotDefined(args.isOpen)) {
        return null;
    }

    let content;

    const commonArgs = {
        documentBindingContext: args.storage?.data.bindingContext,
        numberOurs: args.isInbox ? args.inboxName : args.storage.getValueByPath("NumberOurs") as string,
        entityType: args.isInbox ? EntityTypeName.InboxFile : args.storage.data.bindingContext.getEntityType().getName() as EntityTypeName,
        isInDialog: true,
        isDraft: args.isDraft,
        id: args.id ?? args.storage?.data.bindingContext.getKey(),
        onClose: args.onClose
    };

    if (args.user?.id) {
        const oData = args.storage?.oData ?? args.oData;
        const bc = createBindingContext(EntitySetName.Chats, oData.metadata).addKey(0, true);

        content = (
            <PlugAndPlayForm
                getDef={getDialogDefinitions}
                t={i18next.t}
                bindingContext={bc}
                showButtons={true}
                formViewProps={{
                    formProps: {
                        hideBreadcrumbs: true,
                        hideHeader: false,
                        withoutPadding: false,
                        shouldHideVariant: true,
                        shouldHideAuditTrail: true
                    }
                }}
                initialData={{
                    customData: {
                        user: args.user,
                        useCustomMessages: args.useCustomMessages,
                        chatData: args.chatData,
                        ...commonArgs
                    }
                }}
            />
        );
    } else {
        content = (
            <DialogPage
                rootStorage={args.storage}
                formProps={{
                    shouldHideVariant: true
                }}
                pageViewMode={PageViewMode.Default}
                tableView={ChatsTableView}
                initializeStorage={(storage) => {
                    (storage as TableStorage<IChatEntity, IChatCustomData>).setCustomData({
                        ...commonArgs
                    });
                }}
                getDef={getDialogDefinitions}
                getDefArgs={getDefArgs}
            />
        );
    }

    return (
        <Dialog
            isEditableWindow={true}
            onConfirm={null}
            onClose={() => {
                args.onClose();
            }}>
            {content}
        </Dialog>
    );
};